// Minimal RFC-4180-ish CSV parser. Zero dependency. Targeted at the M10
// admin import flow (Spec V1.1 §M10 L1515-1527 + UB12) — small CSVs (≤500
// rows, ≤10 MB), UTF-8 without BOM, headers on the first line, commas as
// field separators, double quotes around fields containing commas / quotes
// / newlines (escaped by doubling: "" inside a quoted field).
//
// Anything fancier (custom delimiters, multi-line headers, quote rewriting
// inside unquoted fields) is intentionally out of scope — the admin uploads
// files generated either from the downloadable template or from a standard
// spreadsheet export.

const BOM = '﻿';

export const CSV_MAX_ROWS = 500;
export const CSV_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

export type CsvParseSuccess = {
  ok: true;
  headers: string[];
  rows: Record<string, string>[];
};

export type CsvParseError = {
  ok: false;
  code: 'bom' | 'tooLarge' | 'tooManyRows' | 'empty' | 'unterminatedQuote' | 'headerMismatch';
  message: string;
};

export type CsvParseResult = CsvParseSuccess | CsvParseError;

export type ParseOptions = {
  maxRows?: number;
  maxBytes?: number;
};

export function parseCsv(input: string, options: ParseOptions = {}): CsvParseResult {
  const maxRows = options.maxRows ?? CSV_MAX_ROWS;
  const maxBytes = options.maxBytes ?? CSV_MAX_BYTES;

  // Approximate byte size via UTF-16 length × 2; for CSV content typed by
  // humans the result tracks file size within a few percent. Good enough as
  // a safety net — the real per-row cap is the row count.
  if (input.length * 2 > maxBytes) {
    return {
      ok: false,
      code: 'tooLarge',
      message: `File exceeds ${Math.round(maxBytes / 1024 / 1024)} MB cap`,
    };
  }

  if (input.startsWith(BOM)) {
    return {
      ok: false,
      code: 'bom',
      message: 'File starts with a BOM. Save as UTF-8 without BOM and retry.',
    };
  }

  const records = tokenize(input);
  if (records === null) {
    return {
      ok: false,
      code: 'unterminatedQuote',
      message: 'Unterminated quoted field — check for missing " in your CSV.',
    };
  }

  if (records.length === 0) {
    return { ok: false, code: 'empty', message: 'CSV is empty.' };
  }

  const headers = records[0].map((h) => h.trim());
  if (headers.length === 0 || (headers.length === 1 && headers[0] === '')) {
    return { ok: false, code: 'empty', message: 'CSV header line is empty.' };
  }

  const dataRecords = records.slice(1).filter((rec) => !isEmptyRecord(rec));

  if (dataRecords.length > maxRows) {
    return {
      ok: false,
      code: 'tooManyRows',
      message: `CSV has ${dataRecords.length} rows; maximum ${maxRows} per import.`,
    };
  }

  const rows: Record<string, string>[] = [];
  for (const rec of dataRecords) {
    if (rec.length !== headers.length) {
      return {
        ok: false,
        code: 'headerMismatch',
        message: `Row has ${rec.length} columns but header has ${headers.length}.`,
      };
    }
    const obj: Record<string, string> = {};
    for (let i = 0; i < headers.length; i++) {
      obj[headers[i]] = rec[i];
    }
    rows.push(obj);
  }

  return { ok: true, headers, rows };
}

function isEmptyRecord(rec: string[]): boolean {
  return rec.length === 0 || (rec.length === 1 && rec[0] === '');
}

// Single-pass state machine. Tokenizes the entire string into records of
// fields. Returns `null` if a quoted field is left open at EOF.
function tokenize(input: string): string[][] | null {
  const records: string[][] = [];
  let record: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;
  const n = input.length;

  while (i < n) {
    const ch = input[i];

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < n && input[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += ch;
      i += 1;
      continue;
    }

    if (ch === '"' && field === '') {
      inQuotes = true;
      i += 1;
      continue;
    }

    if (ch === ',') {
      record.push(field);
      field = '';
      i += 1;
      continue;
    }

    if (ch === '\r') {
      record.push(field);
      records.push(record);
      record = [];
      field = '';
      i += 1;
      if (i < n && input[i] === '\n') i += 1;
      continue;
    }

    if (ch === '\n') {
      record.push(field);
      records.push(record);
      record = [];
      field = '';
      i += 1;
      continue;
    }

    field += ch;
    i += 1;
  }

  if (inQuotes) return null;

  // Flush the trailing field/record if the file does not end with a newline.
  if (field !== '' || record.length > 0) {
    record.push(field);
    records.push(record);
  }

  return records;
}
